/**
 * Client-side Chat and TChatMessage model-specs with custom methods and computed properties
 */

import type { ReasoningEffort, SkillVersions } from '@elara/domain/flows';
import type { ClientMessagePart } from '@elara/domain/models/schemas/message-schemas';
import type { DataOf, InputOf } from '@elara/firebolt/shared';

import type { FirestoreContainer } from '#containers/FirestoreContainer.js';
import type { ClientInstanceOf } from '#firebase/types.js';
import type { AppFlowsContainer } from '#flows.js';
import type { ChatMessage, ChatMessageModel, NewMessage } from './ChatMessageModel.js';

import { assert, DetailedError } from '@elara/core/errors';
import { ChatModelSpec } from '@elara/domain/models/ChatModelSpec';

import { createClientModel } from '#firebase/createClientModel.js';

/**
 * Chat model for client-side usage
 * Combines the ChatSpec with custom methods and attached flows
 */
export function createClientChatModel(
  flows: AppFlowsContainer,
  firestore: FirestoreContainer,
  Message: ChatMessageModel,
) {
  const model = createClientModel(
    firestore,
    ChatModelSpec,
    () => ({
      /** Check if a user is a participant in the chat */
      isUserParticipant(userId: string): boolean {
        return this.participants.some((p) => p.id === userId);
      },
      /** Check if a user is the owner of the chat */
      isOwner(userId: string): boolean {
        return this.participants.some((p) => p.id === userId && p.role === 'owner');
      },
      /** Get the display name for the chat (uses title or generates from participants) */
      get displayName(): string {
        if (this.title && this.title !== 'New Trip') return this.title;
        return 'New Trip';
      },
      /** Get the number of participants in the chat */
      get participantCount(): number {
        return this.participants.length;
      },
      /** A computed signal that returns `true` if the chat is archived. */
      get isArchived(): boolean {
        return !!this.archivedAt;
      },
      messagesInFlight: [] as ChatMessage[],
      /**
       * Called when a Firestore message matches an in-flight optimistic message.
       * Handles the transition from optimistic to persisted state with defensive content preservation.
       * @param message - The in-flight message that has landed
       * @param loadedMessages - The current Firestore messages to check content completeness
       */
      messageHasLanded(message: ChatMessage, loadedMessages?: ChatMessage[]) {
        const inFlightMsg = this.messagesInFlight.find((m) => m.id === message.id);
        if (!inFlightMsg) return;

        // The Firestore placeholder/final document may land before the client has drained buffered
        // SSE chunks. The stream remains the owner until sendMessage's finally block reconciles it;
        // clearing optimistic state here would let Firestore hydrate content mid-stream and the
        // remaining chunks would be appended a second time.
        if (inFlightMsg.isModelMessage && inFlightMsg.isOptimistic) return;

        if (loadedMessages && inFlightMsg.content.length > 0) {
          const firestoreMsg = loadedMessages.find((m) => m.id === message.id);
          if (firestoreMsg && firestoreMsg.content.length < inFlightMsg.content.length) {
            console.debug(
              `[ChatModel] Firestore message has less content (${firestoreMsg.content.length}) than in-flight (${inFlightMsg.content.length}), waiting...`,
            );
            return;
          }
        }
        inFlightMsg.__optimisticState = undefined;
        setTimeout(() => {
          this.messagesInFlight = this.messagesInFlight.filter((m) => m?.id !== message.id);
        }, 100);
      },
      /** Attached flow for sending a message */
      async sendMessage(
        newMessage: NewMessage,
        {
          agent = 'concierge',
          abortSignal,
          modelId,
          promptVersion,
          reasoningEffort,
          skillVersions,
        }: {
          agent?: string;
          abortSignal: AbortSignal;
          modelId?: string;
          promptVersion?: number;
          reasoningEffort?: ReasoningEffort;
          skillVersions?: SkillVersions;
        },
      ) {
        assert(this.id, 'Chat has to be saved to send a message');
        // Derive optimistic sequence from existing messages so in-flight entries
        // sort after all already-loaded items (server assigns the real value).
        const loadedMessages = Message.list({ chatId: this.id });
        const maxLoadedSeq = loadedMessages.items.reduce((max, m) => Math.max(max, m.sequence ?? 0), 0);
        const message = Message.createMessage(newMessage);
        message.sequence = maxLoadedSeq + 1;
        message.__optimisticState = 'sending';
        this.messagesInFlight.push(message);
        const reply = Message.createOptimisticModelReplyMessage({
          chatId: this.id,
          replyTo: message.id,
          sequence: message.sequence + 1,
          userId: this.userId,
        });
        this.messagesInFlight.push(reply);

        try {
          const { stream } = await flows.sendMessage.stream(
            {
              ...message,
              sequence: message.sequence ?? maxLoadedSeq + 1,
              agent,
              replyId: reply.id,
              content: message.content as ClientMessagePart[],
              modelId,
              promptVersion,
              reasoningEffort,
              skillVersions,
            },
            { signal: abortSignal },
          );
          // Server acknowledged the message - clear optimistic state immediately
          // so user sees their message as "sent" while the model response streams
          message.__optimisticState = undefined;
          reply.__optimisticState = 'streaming';

          for await (const replyPart of stream) {
            const text =
              'text' in replyPart ? replyPart.text : 'reasoning' in replyPart ? replyPart.reasoning : undefined;
            console.debug(`[Model response chunk streamed] ${text ?? ''}`, { replyPart });
            if ('failed' in replyPart) {
              console.error(`Failure received in model response: ${replyPart.error}`, {
                error: replyPart.failed,
                replyPart,
              });
              throw new DetailedError(replyPart.error, { cause: '[Server-Side]', details: replyPart.details });
            } else {
              reply.appendPart(replyPart);
            }
          }
          console.debug('[ChatModel] Stream completed normally');
        } catch (error) {
          // A stopped/torn-down request aborts the fetch, which throws an AbortError out of the
          // stream. That's intentional, not a failure: resolve cleanly so the send path doesn't
          // surface it as an error. The server persists the partial reply (marked stopped), which
          // the Firestore listener reconciles.
          if (abortSignal.aborted || (error instanceof Error && error.name === 'AbortError')) {
            console.debug('[ChatModel] Stream stopped');
            return;
          }
          throw error;
        } finally {
          // Reconcile any final Firestore snapshot that arrived before buffered SSE consumption
          // completed, then release local stream ownership. This ordering prevents the remaining
          // chunks from being appended onto an already-complete persisted response.
          // This also covers the request itself failing: iOS can tear the connection down while the
          // app is suspended after the server has already accepted the message, and a reply left
          // optimistic would keep showing as in progress and hold back the persisted content forever.
          reply.settleStreamingContent();
        }
      },
      /**
       * Asks the server to stop the in-progress AI reply `replyId`. Server-driven (see
       * StopGenerationFlow): flags the reply doc so the generating instance aborts and persists the
       * partial reply as stopped. We intentionally do not abort the local fetch — the SSE stream
       * ends cleanly once the server acts, and the persisted partial reconciles via Firestore.
       */
      async stopGeneration(replyId: string): Promise<{ stopped: boolean; preGeneration: boolean }> {
        assert(this.id, 'Chat has to be saved to stop generation');
        const result = await flows.stopGeneration({ chatId: this.id, replyId });
        return result.data;
      },
    }),
    {
      /** Attached flow for creating a new chat */
      create: flows.createChat,
      /** Attached flow for deleting (archiving) a chat */
      delete: flows.deleteChat,
      /** Attached flow for deleting a message from a chat (privileged only) */
      deleteMessage: flows.deleteChatMessage,
      /** Attached flow for restoring an archived chat */
      restore: flows.restoreChat,
    },
  );
  return model;
}

export type ChatModel = ReturnType<typeof createClientChatModel>;

export interface Chat extends ClientInstanceOf<ChatModel> {}

export interface ChatData extends DataOf<ChatModelSpec> {}

export interface ChatInput extends InputOf<ChatModelSpec> {}
